import { Injectable, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { InstrumentationService } from '../instrumentation/instrumentation.service';
import { ObservableScope } from '../models/observable-scope.enum';
import { AngularEvents } from '../shared-context/angular-events';
import { ContextService } from '../shared-context/context.service';
import { BaseUpdateContentService, ContentUpdate } from './base-update-content.service';
import { DocumentService } from './document.service';
import { EventService } from './event.service';
import { OfficeRouteService } from './office-route.service';
import { PageVisibilityService } from './page-visibility.service';

@Injectable()
export class FocusUpdateContentService extends BaseUpdateContentService implements OnDestroy {

    private readonly updateInterval: number;
    private updateTimer: ReturnType<typeof setInterval>;

    private readonly focusSubscription: Subscription;

    constructor(
        contextService: ContextService,
        private readonly documentService: DocumentService,
        eventService: EventService,
        protected readonly instrumentationService: InstrumentationService,
        officeRouteService: OfficeRouteService,
        private readonly pageVisibilityService: PageVisibilityService
    ) {
        super(contextService, instrumentationService, officeRouteService);

        this.updateInterval = contextService.sharedContext.dataUpdateIntervalInSeconds * BaseUpdateContentService.MsPerSecond;
        if (contextService.sharedContext.updateDataOnViewEnabled) {
            this.focusSubscription = eventService.events.withScope(ObservableScope.OnlyFuture).pipe(
                filter(event => event.name === AngularEvents.windowFocused)).subscribe(() => {
                    const activeElement = this.documentService.nativeDocument.activeElement;
                    if (activeElement && activeElement.tagName === 'IFRAME') {
                        // Iframe usage (share control in particular) can result in focus transition between app window and iframe
                        return;
                    }

                    if (this.updateInterval) {
                        this.resetUpdateTimer();
                    }
                    this.broadcastUpdates();
                });
        }

        if (this.updateInterval) {
            this.resetUpdateTimer();
        }
    }

    public ngOnDestroy(): void {
        if (this.focusSubscription) {
            this.focusSubscription.unsubscribe();
        }
    }

    private resetUpdateTimer() {
        if (typeof this.updateTimer === 'number') {
            clearInterval(this.updateTimer);
        }

        this.updateTimer = setInterval(() => {
            if (this.pageVisibilityService.isVisible()) {
                this.broadcastUpdates();
            }
        }, this.updateInterval);
    }

    private broadcastUpdates() {
        const currentRoute = this.officeRouteService.getRouteItemForCurrentLocation();
        this.updates.forEach((update: ContentUpdate<any>, key: string) => {
            if (OfficeRouteService.areRoutesEqual(currentRoute, update.route)) {
                this.safeUpdate(update, key);
            }
        });
    }
}
